import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { AuthService } from 'src/app/service/auth.service';
import { SharedFileService } from 'src/app/service/shared-file.service';
import { PlanModalService } from 'src/app/share/plan-modal.service';
import { SharedFileInfoRes } from 'src/app/model/response/shared-file';

type PageState = 'loading' | 'available' | 'not_found' | 'expired';

/**
 * Página pública do link de arquivo (/shared?code=...). Baixar funciona sem login;
 * Salvar exige conta: login ou criação de conta voltam para esta URL com `save=1`,
 * e a página salva sozinha ao voltar (criar conta → ativar → login automático
 * também passa pelo postLoginRedirect).
 */
@Component({
  selector: 'app-shared-file',
  templateUrl: './shared-file.component.html',
  styleUrls: ['./shared-file.component.css'],
})
export class SharedFileComponent implements OnInit, OnDestroy {
  state: PageState = 'loading';
  info: SharedFileInfoRes | null = null;
  code = '';
  /** Segundos restantes, recalculados a partir de `expiresAt` (não de `ttlSeconds`). */
  remainingSeconds = 0;
  saving = false;
  saved = false;
  isLoggedIn = false;

  private expiresAtMs = 0;
  /** Voltou do login/cadastro iniciado pelo Salvar: salva uma vez ao carregar. */
  private autoSave = false;
  private timer: ReturnType<typeof setInterval> | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sharedFileService: SharedFileService,
    private authService: AuthService,
    private planModalService: PlanModalService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private titleService: Title,
  ) {}

  ngOnInit(): void {
    this.translocoService
      .selectTranslate('title.shared-file')
      .subscribe((value) => this.titleService.setTitle(value));

    this.isLoggedIn = !!this.authService.getUserInformations();
    const params = this.route.snapshot.queryParamMap;
    this.code = params.get('code') ?? '';
    this.autoSave = params.get('save') === '1';
    if (!this.code) {
      this.state = 'not_found';
      return;
    }
    this.sharedFileService.getInfo(this.code).subscribe({
      next: (info) => {
        this.info = info;
        this.expiresAtMs = Date.parse(info.expiresAt);
        this.state = 'available';
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
        if (this.autoSave) this.runAutoSave();
      },
      error: (err: HttpErrorResponse) => {
        this.state = err.status === 410 ? 'expired' : 'not_found';
      },
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  /** `createdDate` vem sem fuso, mas é UTC: acrescenta o `Z` para o date pipe converter. */
  get createdDateUtc(): string | null {
    const raw = this.info?.createdDate;
    if (!raw) return null;
    return /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`;
  }

  get remainingLabel(): string {
    const s = this.remainingSeconds;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
  }

  download(): void {
    if (this.isExpiredNow()) return;
    window.location.href = this.sharedFileService.downloadUrl(this.code);
  }

  save(): void {
    if (this.isExpiredNow() || this.saving) return;
    const userId = this.authService.getUserInformations();
    if (!userId) {
      this.goLogin();
      return;
    }
    this.saving = true;
    this.sharedFileService.saveToAccount(userId, this.code).subscribe({
      next: () => {
        this.saving = false;
        this.saved = true;
        this.notify('success', 'sharedFile.saveSuccess');
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.handleSaveError(err);
      },
    });
  }

  goArchives(): void {
    this.router.navigate(['/archives']);
  }

  goAssistant(): void {
    this.router.navigate(['/ftassistant']);
  }

  goCreateAccount(): void {
    this.authService.setPostLoginRedirect(this.returnUrlWithSave());
    this.router.navigate(['/create-account']);
  }

  /**
   * Tira o `save=1` da URL antes de salvar: um F5 depois disso não salva de novo
   * (o backend não deduplica — cada save é uma cópia nova).
   */
  private runAutoSave(): void {
    this.autoSave = false;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { save: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (this.isLoggedIn && this.state === 'available') this.save();
  }

  private returnUrlWithSave(): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['/shared'], { queryParams: { code: this.code, save: 1 } }),
    );
  }

  private handleSaveError(err: HttpErrorResponse): void {
    const code = err.error?.error;
    if (err.status === 410) {
      this.markExpired();
    } else if (err.status === 404) {
      this.stopTimer();
      this.state = 'not_found';
    } else if (err.status === 409 && code === 'already_owner') {
      this.notify('warn', 'sharedFile.alreadyOwner');
    } else if (err.status === 403 && code === 'storage_limit_exceeded') {
      this.notify('warn', 'sharedFile.storageLimit');
      this.planModalService.open();
    } else if (err.status === 401 || err.status === 403) {
      this.goLogin();
    } else {
      this.notify('error', 'sharedFile.saveError');
    }
  }

  private goLogin(): void {
    this.authService.setPostLoginRedirect(this.returnUrlWithSave());
    this.router.navigate(['/login']);
  }

  private tick(): void {
    this.remainingSeconds = Math.max(0, Math.floor((this.expiresAtMs - Date.now()) / 1000));
    if (this.remainingSeconds === 0) this.markExpired();
  }

  /** A página pode ter ficado aberta além da validade: confere o relógio antes de agir. */
  private isExpiredNow(): boolean {
    if (Date.now() < this.expiresAtMs) return false;
    this.markExpired();
    return true;
  }

  private markExpired(): void {
    this.stopTimer();
    this.state = 'expired';
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }

  private notify(severity: 'success' | 'warn' | 'error', key: string): void {
    this.messageService.add({ key: 'notification', severity, summary: this.translocoService.translate(key) });
  }
}
